import i18n from 'i18next';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { processTutoringRequest } from '../../tutoring/shared/Constants';
import { historyPush, historyReplace } from '../../services/History';
import { getUserId } from '../profileSettings/Selectors';
import { listsActionTypes } from './Constants';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as createFolderActions from '../../components/modals/createFolder/Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as sidebarActions from '../../components/sideBar/Actions';
import * as listsActions from './Actions';
import * as sharedActions from '../../pagesNext/shared/Actions';
import { recentItems } from './Selectors';

export default function* () {
	yield all([
		yield takeLatest(listsActionTypes.GET_RECENT_LISTS_REQUEST, handleRecentListsRequest),
		yield takeLatest(listsActionTypes.GET_RECENT_ITEMS_REQUEST, handleRecentItemsRequest),
		yield takeLatest(listsActionTypes.GET_MADE_LISTS_REQUEST, handleMadeListsRequest),
		yield takeLatest(listsActionTypes.GET_FOLDERS_REQUEST, handleFoldersRequest),
		yield takeLatest(listsActionTypes.GET_RECENT_LESSONS_REQUEST, handleRecentLessonsRequest),
		yield takeLatest(listsActionTypes.CREATE_FOLDER_REQUEST, handleCreateFolderRequest),
		yield takeLatest(
			listsActionTypes.RESEND_EMAIL_VERIFICATION_REQUEST,
			handleResendEmailVerificationRequest,
		),
		yield takeLatest(listsActionTypes.CONFIRM_EMAIL_REQUEST, handleConfirmEmailRequest),
		yield takeLatest(
			listsActionTypes.HIDE_ONBOARDING_TASKS_REQUEST,
			handleHideOnboardingTasksRequest,
		),
		yield takeLatest(listsActionTypes.HIDE_RECENT_ITEM_REQUEST, handleHideRecentItemRequest),
	]);
}

export function* handleRecentListsRequest({ payload }) {
	try {
		const { subject_id } = payload || {};

		const { data } = yield call(processRequest, 'items_overview', 'GET', { subject_id });

		yield put(listsActions.getRecentListsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(listsActions.getRecentListsError());
	}
}

export function* handleRecentItemsRequest({ payload }) {
	try {
		const { subject_id, offset } = payload || {};

		const { data } = yield call(processRequest, `recent_items`, 'GET', { offset, subject_id });

		const formattedData = { ...data };

		if (offset) {
			const recentItemsData = yield select(recentItems);
			formattedData.recent_items = [
				...recentItemsData.recent_items,
				...formattedData.recent_items,
			];
		}

		yield put(listsActions.getRecentItemsSuccess(formattedData));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(listsActions.getRecentItemsError());
	}
}

export function* handleMadeListsRequest({ payload }) {
	const { queries } = payload || {};
	try {
		const { data } = yield call(processRequest, 'practiceable_items/created', 'GET', queries);

		yield put(listsActions.getMadeListsSuccess(data.created_items));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(listsActions.getMadeListsError());
	}
}

export function* handleFoldersRequest() {
	try {
		const { data } = yield call(processRequest, 'folders');

		yield put(listsActions.getFoldersSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(listsActions.getFoldersError());
	}
}

export function* handleRecentLessonsRequest({ payload }) {
	try {
		const { userId } = payload || {};
		const { data } = yield call(processTutoringRequest, `users/${userId}/scheduled_lessons`);

		yield put(listsActions.getRecentLessonsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(listsActions.getRecentLessonsError());
	}
}

export function* handleCreateFolderRequest(action) {
	try {
		const { options, noRedirect } = action.payload;
		const requestPayload = {
			folder: {
				...options,
			},
		};
		const { data } = yield call(processRequest, 'folders', 'POST', requestPayload);
		yield put(createFolderActions.toggleCreateFolderModal(false));

		if (!noRedirect) yield call(historyPush, `/folders/${data.folder.id}`);

		yield put(sharedActions.getUserCollections('folders'));
		yield put(
			notificationActions.createNotification(
				i18n.t('lists.create_folder_notification'),
				null,
				false,
				5000,
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(listsActions.createFolderError());
	}
}

export function* handleResendEmailVerificationRequest(action) {
	try {
		const { userId, email } = action.payload;

		yield call(processRequest, `users/${userId}/resend_confirmation_email`, 'POST');
		yield put(listsActions.resendEmailVerificationSuccess());

		yield put(sidebarActions.getUseDataRequest());
		yield put(
			notificationActions.createNotification(
				i18n.t('lists.teacher_validation.resend_message', { email }),
				null,
				false,
				5000,
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(listsActions.resendEmailVerificationError());
	}
}

export function* handleConfirmEmailRequest(action) {
	try {
		const { accountId, confirmationToken } = action.payload;

		yield call(processRequest, `public/user_accounts/${accountId}/confirm_email`, 'POST', {
			confirmation_token: confirmationToken,
		});
		yield call(historyReplace, '/success-email-verification');
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield call(historyReplace, '/');
		yield put(
			notificationActions.createNotification(
				i18n.t('lists.teacher_validation.error_message'),
				null,
				true,
				5000,
			),
		);
	}
}

export function* handleHideOnboardingTasksRequest() {
	try {
		const userId = yield select(getUserId);

		yield call(processRequest, `users/${userId}`, 'PUT', {
			user: {
				hide_onboarding: true,
			},
		});

		yield put(sidebarActions.getUseDataRequest());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleHideRecentItemRequest({ payload }) {
	try {
		const { link, callback } = payload || {};
		const { type, id } = link || {};

		yield call(processRequest, 'items_overview', 'DELETE', { type, id });
		yield put(listsActions.hideItemSuccess(type, id));

		if (callback) callback();
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}
